/**
 * Composable for managing guest mode functionality
 */

// Type for guest budget data
interface GuestBudgetData {
  month?: number
  year?: number
  isGuestBudget?: boolean
  createdAt?: string
  [key: string]: any
}

export const useGuestMode = () => {
  // State to track if user is in guest mode
  const isGuestMode = useState('isGuestMode', () => false)
  
  // Guest budget data (stored in browser memory)
  const guestBudget = useState<GuestBudgetData | null>('guestBudget', () => null)

  /**
   * Check if the current user is in guest mode
   */
  const checkGuestMode = () => {
    const { status } = useAuth()
    return status.value !== 'authenticated'
  }

  /**
   * Initialize guest mode
   */
  const initGuestMode = () => {
    isGuestMode.value = true
    
    // Check localStorage for saved budget
    if (process.client) {
      const savedBudget = localStorage.getItem('guestBudget')
      if (savedBudget) {
        try {
          guestBudget.value = JSON.parse(savedBudget)
        } catch (error) {
          console.error('Failed to parse saved guest budget', error)
          localStorage.removeItem('guestBudget')
          localStorage.removeItem('isFromGuestMode')
          guestBudget.value = null
        }
      } else {
        guestBudget.value = null
      }
    } else {
      // On server, just make sure budget is null
      guestBudget.value = null
    }
  }

  /**
   * Exit guest mode (when user signs in)
   */
  const exitGuestMode = () => {
    isGuestMode.value = false
    
    // We don't clear localStorage here to allow for post-login handling
    // Just clear the in-memory state
    guestBudget.value = null
  }
  /**
   * Save budget data in guest mode (memory and localStorage)
   */  
  const saveGuestBudget = (budgetData: GuestBudgetData) => {
    
    
    // Extract month and year from dateOfPay if available
    let month = new Date().getMonth() + 1
    let year = new Date().getFullYear()
    
    if (budgetData.payDay?.dateOfPay) {
      const payDate = new Date(budgetData.payDay.dateOfPay)
      month = payDate.getMonth() + 1
      year = payDate.getFullYear()
    }
    
    // Add isGuestBudget, createdAt, month, and year fields
    const enhancedBudget = {
      ...budgetData,
      month,
      year,
      isGuestBudget: true,
      createdAt: new Date().toISOString()
    }
    
    // Save to in-memory state
    guestBudget.value = enhancedBudget
      // Save to localStorage if on client
    if (process.client) {
      try {
        localStorage.setItem('guestBudget', JSON.stringify(enhancedBudget))
        // Set flag to indicate this budget came from guest mode
        localStorage.setItem('isFromGuestMode', 'true')
      } catch (error) {
        console.error('Failed to save guest budget to localStorage', error)
      }
    }
  }

  /**
   * Check if we have guest budget data (in memory or localStorage)
   */
  const hasGuestBudget = computed(() => {
    // Check in-memory first
    if (guestBudget.value !== null) {
      return true
    }
    
    // Then check localStorage if on client
    if (process.client) {
      return localStorage.getItem('guestBudget') !== null
    }
    
    return false
  })

  /**
   * Get export-friendly budget data for guest users
   */  
  const getGuestBudgetForExport = () => {
    // Return in-memory budget if available
    if (guestBudget.value) {
      return {
        ...(guestBudget.value as GuestBudgetData),
        id: 'guest-budget',
        userId: 'guest',
        month: guestBudget.value.month,
        year: guestBudget.value.year
      }
    }
    
    // Try to get from localStorage if on client
    if (process.client) {
      const savedBudget = localStorage.getItem('guestBudget')
      if (savedBudget) {
        try {
          const parsedBudget = JSON.parse(savedBudget)
          return {
            ...parsedBudget,
            id: 'guest-budget',
            userId: 'guest',
            month: parsedBudget.month,
            year: parsedBudget.year
          }
        } catch (error) {
          console.error('Failed to parse saved guest budget for export', error)
        }
      }
    }
    
    return null
  }
  
  /**
   * Get saved guest budget from localStorage
   */
  const getSavedGuestBudget = () => {
    if (process.client) {
      const savedBudget = localStorage.getItem('guestBudget')
      if (savedBudget) {
        try {
          return JSON.parse(savedBudget)
        } catch (error) {
          console.error('Failed to get saved guest budget', error)
        }
      }
    }
    return null
  }
  
  /**
   * Clear guest budget data from localStorage after transfer to user account
   */
  const clearGuestBudget = () => {
    guestBudget.value = null
    if (process.client) {
      localStorage.removeItem('guestBudget')
      localStorage.removeItem('isFromGuestMode')
    }
  }

  return {
    isGuestMode: readonly(isGuestMode),
    guestBudget: readonly(guestBudget),
    hasGuestBudget,
    checkGuestMode,
    initGuestMode,
    exitGuestMode,
    saveGuestBudget,
    getGuestBudgetForExport,
    getSavedGuestBudget,
    clearGuestBudget
  }
}
